import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  SlidersHorizontal, ArrowUp, ArrowDown, Pencil, Trash2, Plus, RotateCcw, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { MENU_ITEMS, loadMenuLayout, saveMenuLayout, resetMenuLayout } from '../../lib/adminMenu';

function move(arr, idx, dir) {
  const other = idx + dir;
  if (other < 0 || other >= arr.length) return arr;
  const next = [...arr];
  [next[idx], next[other]] = [next[other], next[idx]];
  return next;
}

export default function AdminMenuLayoutPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMenuLayout(user?.id).then((g) => setGroups(g.map((x) => ({ ...x, items: [...x.items] }))));
  }, [user?.id]);

  const update = (next) => { setGroups(next); setDirty(true); };

  const moveGroup = (idx, dir) => update(move(groups, idx, dir));

  const moveItem = (gIdx, iIdx, dir) => {
    const next = groups.map((g) => ({ ...g, items: [...g.items] }));
    next[gIdx].items = move(next[gIdx].items, iIdx, dir);
    update(next);
  };

  const moveItemToGroup = (gIdx, iIdx, targetId) => {
    const next = groups.map((g) => ({ ...g, items: [...g.items] }));
    const [path] = next[gIdx].items.splice(iIdx, 1);
    next.find((g) => g.id === targetId).items.push(path);
    update(next);
  };

  const startRename = (g) => { setRenamingId(g.id); setRenameValue(g.title); };
  const applyRename = () => {
    if (!renameValue.trim()) return;
    update(groups.map((g) => (g.id === renamingId ? { ...g, title: renameValue.trim() } : g)));
    setRenamingId(null);
  };

  const addGroup = () => {
    update([...groups, { id: `custom-${Date.now()}`, title: 'New Group', items: [] }]);
  };

  const removeGroup = (g) => {
    if (g.items.length > 0) { toast.error('Move all pages out of this group first'); return; }
    update(groups.filter((x) => x.id !== g.id));
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveMenuLayout(groups, user?.id);
      setGroups(saved.map((x) => ({ ...x, items: [...x.items] })));
      setDirty(false);
      toast.success('Menu layout saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save layout');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset the menu to the standard layout? Your custom arrangement will be lost.')) return;
    try {
      const def = await resetMenuLayout(user?.id);
      setGroups(def.map((x) => ({ ...x, items: [...x.items] })));
      setDirty(false);
      toast.success('Menu reset to default');
    } catch { toast.error('Failed to reset'); }
  };

  if (!groups) return <AdminLayout><div className="p-6 text-sm text-muted-foreground">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6 space-y-6 max-w-3xl" data-testid="menu-layout-page">
        <div className="border-b border-border pb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <SlidersHorizontal className="w-6 h-6 text-primary" strokeWidth={1.5} /> MENU LAYOUT
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Arrange the sidebar the way you want — reorder groups, rename them, and move pages between them. Nothing can be deleted, only rearranged.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} data-testid="menu-reset-btn" className="rounded-sm">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset to default
            </Button>
            <Button onClick={save} disabled={!dirty || saving} data-testid="menu-save-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
              {saving ? 'Saving…' : 'Save layout'}
            </Button>
          </div>
        </div>

        {groups.map((group, gIdx) => (
          <Card key={group.id} className="rounded-sm border-border" data-testid={`menu-group-${group.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 border-l-2 border-primary pl-3">
                {renamingId === group.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} data-testid="rename-group-input" className="rounded-sm h-8 max-w-xs" autoFocus />
                    <Button size="icon" variant="ghost" onClick={applyRename} data-testid="rename-group-apply" className="h-7 w-7 text-green-400"><Check className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setRenamingId(null)} className="h-7 w-7"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <h2 className="text-sm font-barlow tracking-widest uppercase text-primary">
                    {group.title}
                    <span className="ml-2 text-muted-foreground normal-case tracking-normal">({group.items.length})</span>
                  </h2>
                )}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => moveGroup(gIdx, -1)} disabled={gIdx === 0} data-testid={`group-up-${group.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20 p-1"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => moveGroup(gIdx, 1)} disabled={gIdx === groups.length - 1} data-testid={`group-down-${group.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20 p-1"><ArrowDown className="w-4 h-4" /></button>
                  <Button variant="ghost" size="icon" onClick={() => startRename(group)} data-testid={`rename-group-${group.id}`} className="h-7 w-7"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => removeGroup(group)} disabled={group.items.length > 0} data-testid={`delete-group-${group.id}`} className="h-7 w-7 text-red-400 hover:text-red-300 disabled:opacity-20"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>

              {group.items.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-3">Empty group — move pages here or delete it.</p>
              ) : (
                <div className="space-y-1.5">
                  {group.items.map((path, iIdx) => {
                    const item = MENU_ITEMS[path];
                    if (!item) return null;
                    return (
                      <div key={path} className="flex items-center gap-2 bg-muted/30 border border-border rounded-sm px-3 py-2" data-testid={`menu-item-${path.replace(/\//g, '-')}`}>
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <button onClick={() => moveItem(gIdx, iIdx, -1)} disabled={iIdx === 0} className="text-muted-foreground hover:text-primary disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => moveItem(gIdx, iIdx, 1)} disabled={iIdx === group.items.length - 1} className="text-muted-foreground hover:text-primary disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
                        </div>
                        <item.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                        <span className="text-sm text-foreground flex-1 min-w-0 truncate">{item.label}</span>
                        <Select value={group.id} onValueChange={(v) => v !== group.id && moveItemToGroup(gIdx, iIdx, v)}>
                          <SelectTrigger className="rounded-sm h-8 w-40 text-xs flex-shrink-0" data-testid={`move-select-${path.replace(/\//g, '-')}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-between items-center pb-8">
          <Button variant="outline" onClick={addGroup} data-testid="add-group-btn" className="rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Add Group
          </Button>
          <Button onClick={save} disabled={!dirty || saving} data-testid="menu-save-btn-bottom" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
            {saving ? 'Saving…' : 'Save layout'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
